import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import blinkscribeapi from "../api/blinkscribeapi";
import ToastMessage from "../utils/mui/righttoptoast";
import Editscreen from "../blinkscribe/archeivescreens/editscreen";
import SanityCheckPanel from "./SanityCheckPanel";
import BillingTab from "./BillingTab";
import { FormattedNoteText, FormattedNoteList } from "../utils/noteFormat";
import { DateField } from "../components/calendar/CalendarPanel";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField } from "@mui/material";
import {
  isNoteSigned,
  noteVisitMismatch,
  noteVisitLabel,
  normalizeVisitOptions,
  markRecordingDay,
  optionText,
  moveConfirmText,
  supportsPrimaryOverride,
  primaryProviderName,
  visitProviderName,
  primaryProviderBody,
  needsSignMismatchConsent,
  mismatchDetailsFromNote,
  unsignedSiblingIds,
  combineFirstText,
  recordingDayKey,
  moveRequirements,
  postedAckText,
  existingAckText,
  refusalNeeds,
  movedMessage,
  providersById,
  providerAliases,
  providerKey,
  dedupeProviders,
  refKey,
  sameProvider,
  providerRef,
  noteMatchesProvider,
} from "./noteVisit";
import { recorderTag } from "../utils/otherUserNote";
import { shortDate } from "../utils/visitGuard";
import { requestSignMismatchConsent } from "../components/VisitConsent/VisitConsentHost";
import { signOffNote } from "../components/VisitConsent/signOffGuard";
import { hasMultipleDepartments } from "../config/practice";

// SOAP display labels — mirrors LiveScribePanel so the review note reads the same.
const SOAP_LABELS = {
  chief_complaint: "Chief Complaint",
  hpi: "History of Present Illness",
  review_of_systems: "Review of Systems",
  physical_examination: "Physical Examination",
  assessment_plan: "Assessment & Plan",
  current_medications: "Current Medications",
  allergies: "Allergies",
  doctor_instructions: "Doctor Instructions",
};

const stripHtml = (str) => (typeof str === "string" ? str.replace(/<[^>]+>/g, "").trim() : str);

const formatItem = (item) => {
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null) {
    const diag = item.diagnosis || item.condition || item.problem || item.assessment || item.name || "";
    const plan = item.plan || item.recommendation || item.treatment || item.note || "";
    const parts = [diag, plan].filter(Boolean);
    if (parts.length) return parts.join(" — ");
    const vals = Object.values(item).filter((v) => typeof v === "string" && v.trim());
    return vals.length ? vals.join(" — ") : JSON.stringify(item);
  }
  return String(item);
};

// Flatten a note_json into searchable plain text.
const noteToText = (nj) => {
  if (!nj || typeof nj !== "object") return "";
  const parts = [];
  Object.values(nj).forEach((v) => {
    if (typeof v === "string") parts.push(v);
    else if (Array.isArray(v)) parts.push(v.map(formatItem).join(" "));
  });
  return stripHtml(parts.join(" ")).toLowerCase();
};

const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// The provider a daily-review note is listed under, as a provider reference.
const noteProvider = (note) =>
  note && note.providername ? { providername: note.providername, providerid: note.providerid } : null;

// Mirror of backend build_attestation_lines for live preview.
const buildAttestations = (providers, discussedWith) => {
  const names = (providers || []).map((p) => p.providername).filter(Boolean);
  const lines = [];
  if (discussedWith && discussedWith.providername) {
    lines.push(`Case discussed with ${discussedWith.providername}, supervising physician, who concurs with the assessment and plan.`);
    if (!names.includes(discussedWith.providername)) names.push(discussedWith.providername);
  }
  if (names.length) {
    const d = new Date();
    const date = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
    lines.push(`This note has been reviewed and digitally signed by ${names.join(", ")} on ${date}.`);
  }
  return lines;
};

const StatusBadge = ({ status }) => {
  const map = {
    APPROVED: { label: "Signed", cls: "bg-green-100 text-green-700" },
    Summarized: { label: "Ready to sign", cls: "bg-amber-100 text-amber-700" },
    Processing: { label: "Processing", cls: "bg-gray-100 text-gray-600" },
    Submitted: { label: "Processing", cls: "bg-gray-100 text-gray-600" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
};

const NoteReviewScreen = () => {
  const [date, setDate] = useState(todayStr());
  const [providerFilter, setProviderFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  // Provider lists as the server sent them. The canonical server lists one
  // entry per providerid with its aliases; an older server lists one per
  // spelling (see noteVisit.js "Provider identity").
  const [providers, setProviders] = useState([]); // providers seen this day (filter dropdown)
  const [allProviders, setAllProviders] = useState([]); // full practice list (assign/supervise)
  const [notes, setNotes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [metaById, setMetaById] = useState({}); // id -> { providers:[], discussedWith:obj|null }
  // "Visit" mover: the patient's appointments around each note's recording
  // day (BlinkScribe GET /v1/recording/{id}/visit-options), cached by note id;
  // the move awaiting confirmation; the note being moved.
  const [visitOptsById, setVisitOptsById] = useState({}); // id -> { loading, error, current, options }
  const visitOptsRequestedRef = useRef(new Set());
  // { note, target, needPosted, needExisting, ackPosted, ackExisting, reason, error }
  const [pendingMove, setPendingMove] = useState(null);
  const [movingId, setMovingId] = useState(null);
  // "Change provider": { note, choice (providerKey), saving, error }
  const [providerDialog, setProviderDialog] = useState(null);

  const [view, setView] = useState("notes"); // 'notes' | 'billing' — billing is additive
  // Department filter defaults to the logged-in user's department
  // (QA 2026-08-27); "" = all departments.
  const userConfig = useSelector((state) => state.userconfig);
  const [deptFilter, setDeptFilter] = useState(undefined);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const closeToast = (_e, reason) => { if (reason !== "clickaway") setToast((t) => ({ ...t, open: false })); };

  // Id mode when either list is canonical (both come from the same server;
  // the day's list may be empty). Providers are then one entry per id and
  // compared by id; otherwise by name, as before this change.
  const idMode = useMemo(() => providersById(allProviders) || providersById(providers), [allProviders, providers]);
  // The practice list (assign / supervise / change provider) and the day's
  // list (filter), each one entry per provider; the union resolves names.
  const practiceProviders = useMemo(() => dedupeProviders(allProviders, idMode), [allProviders, idMode]);
  const dayProviders = useMemo(() => dedupeProviders(providers, idMode), [providers, idMode]);
  const providerIndex = useMemo(
    () => dedupeProviders([...allProviders, ...providers], idMode),
    [allProviders, providers, idMode],
  );
  const keyOf = useCallback((p) => providerKey(p, idMode), [idMode]);
  const keyOfRef = useCallback((ref) => refKey(providerIndex, ref, idMode), [providerIndex, idMode]);
  const same = useCallback((a, b) => sameProvider(providerIndex, a, b, idMode), [providerIndex, idMode]);
  const toRef = useCallback((p) => providerRef(providerIndex, p, idMode), [providerIndex, idMode]);

  // Default supervising provider = first matching "Madhu", else first in list.
  const defaultSupervisor = useMemo(() => {
    const m = practiceProviders.find((p) => providerAliases(p).some((a) => /madhu/i.test(a)));
    return m || practiceProviders[0] || null;
  }, [practiceProviders]);

  useEffect(() => {
    if (deptFilter === undefined && userConfig?.config) {
      const d = Number(userConfig.config.departmentid);
      setDeptFilter(Number.isFinite(d) && d > 0 ? d : "");
    }
  }, [userConfig, deptFilter]);

  useEffect(() => {
    blinkscribeapi.get("/v1/provider/list")
      .then((res) => { if (res?.status === 200) setAllProviders(res.data || []); })
      .catch(() => {});
  }, []);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        date_filter: { start_date: date, end_date: date },
        statuses: ["Summarized", "APPROVED"],
      };
      if (deptFilter !== "" && deptFilter !== undefined) payload.departmentid = deptFilter;
      const res = await blinkscribeapi.post("/v1/provider/recording/daily-review", payload);
      if (res?.status === 200) {
        setNotes(res.data || []);
        setProviders(res.providers || []);
        setMetaById({}); // reset edits on reload
        // Visits may have moved: reload each note's options when reselected.
        setVisitOptsById({});
        visitOptsRequestedRef.current = new Set();
      } else {
        setNotes([]); setProviders([]);
      }
    } catch (err) {
      console.error("daily-review fetch failed", err);
      showToast(err?.response?.data?.message || "Failed to load notes", "error");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [date, deptFilter]);

  useEffect(() => { if (deptFilter !== undefined) fetchNotes(); }, [fetchNotes, deptFilter]);

  // Derive the editable meta for a note. The appointment provider is
  // authoritative: the persisted list is "primary slot + extras", so we drop
  // its index-0 primary and rebuild as [live appointment provider, ...extras].
  // That way reassigning the provider in appointment_master flows through here
  // (and into the signed attestation) instead of being shadowed by a stale copy.
  // Saved entries are canonicalised (label + id) so an old spelling of the
  // primary ("Reshmi Adupa, MD" next to "Reshmi Adupa") folds into it.
  const initMeta = useCallback((note) => {
    if (!note) return { providers: [], discussedWith: null };
    const primary = note.providername ? toRef(noteProvider(note)) : null;
    const saved = (note.providers && note.providers.length) ? note.providers : [];
    const extras = [];
    for (const p of saved.length ? saved.slice(1) : []) {
      if (!p || !p.providername) continue;
      if (primary && same(p, primary)) continue;
      if (extras.some((x) => same(x, p))) continue;
      extras.push(toRef(p));
    }
    const provs = primary ? [primary, ...extras] : saved.filter((p) => p && p.providername).map(toRef);
    return { providers: provs, discussedWith: note.discussed_with ? toRef(note.discussed_with) : null };
  }, [same, toRef]);

  const getMeta = useCallback((note) => (note && metaById[note.id]) || initMeta(note), [metaById, initMeta]);

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter((n) => {
      if (!noteMatchesProvider(n, providerFilter, providerIndex, idMode)) return false;
      if (!q) return true;
      const hay = `${n.patient_name || ""} ${n.providername || ""} ${noteToText(n.note_json)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [notes, providerFilter, search, providerIndex, idMode]);

  const selectedNote = useMemo(
    () => filteredNotes.find((n) => n.id === selectedId) || filteredNotes[0] || null,
    [filteredNotes, selectedId]
  );

  // Load the selected note's visit options once (unsigned notes only: a
  // signed note is never moved).
  const selectedNoteId = selectedNote?.id;
  const selectedNoteSigned = selectedNote ? isNoteSigned(selectedNote) : true;
  useEffect(() => {
    if (!selectedNoteId || selectedNoteSigned) return;
    if (visitOptsRequestedRef.current.has(selectedNoteId)) return;
    visitOptsRequestedRef.current.add(selectedNoteId);
    const noteForDay = selectedNote;
    setVisitOptsById((prev) => ({ ...prev, [selectedNoteId]: { loading: true } }));
    blinkscribeapi
      .get(`/v1/recording/${selectedNoteId}/visit-options`)
      .then((res) => {
        if (res?.status !== undefined && res.status !== 200) {
          throw Object.assign(new Error(res?.message || "No visits"), { soft: true });
        }
        const norm = markRecordingDay(normalizeVisitOptions(res), noteForDay);
        setVisitOptsById((prev) => ({ ...prev, [selectedNoteId]: { loading: false, ...norm } }));
      })
      .catch((err) => {
        const st = err?.response?.status;
        const raw = err?.response?.data?.message || err?.message;
        const msg =
          st === 404
            ? "Changing the visit is not available on this server yet."
            : (typeof raw === "string" && raw) || "Could not load this patient's visits.";
        setVisitOptsById((prev) => ({ ...prev, [selectedNoteId]: { loading: false, error: msg } }));
      });
    // selectedNote is read only for its recording day, fixed per id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoteId, selectedNoteSigned]);

  const pickVisit = (note, appointmentid) => {
    const opts = visitOptsById[note.id];
    const target = (opts?.options || []).find((o) => o.appointmentid === String(appointmentid));
    if (!target || target.isCurrent || target.disabled) return;
    const needs = moveRequirements(opts, target, note);
    setPendingMove({
      note,
      target,
      needPosted: needs.postedAck,
      needExisting: needs.existingAck,
      ackPosted: false,
      ackExisting: false,
      reason: "",
      error: "",
    });
  };
  const updatePendingMove = (patch) => setPendingMove((pm) => (pm ? { ...pm, ...patch } : pm));
  const moveReady =
    !!pendingMove &&
    (!pendingMove.needPosted || (pendingMove.ackPosted && pendingMove.reason.trim() !== "")) &&
    (!pendingMove.needExisting || pendingMove.ackExisting);

  const confirmMove = async () => {
    const pm = pendingMove;
    if (!pm || movingId || !moveReady) return;
    setMovingId(pm.note.id);
    try {
      const body = { appointmentid: Number(pm.target.appointmentid) };
      if (pm.reason.trim()) body.reason = pm.reason.trim();
      if (pm.needPosted) body.athena_posted_ack = pm.ackPosted;
      if (pm.needExisting) body.allow_existing_note = pm.ackExisting;
      const res = await blinkscribeapi.put(`/v1/recording/${pm.note.id}/visit`, body);
      if (res?.status === 200) {
        showToast(movedMessage(res, pm.target), res?.warnings?.length ? "warning" : "success");
        setPendingMove(null);
        fetchNotes();
      } else {
        showToast((typeof res?.message === "string" && res.message) || "Could not move this note", "info");
        setPendingMove(null);
      }
    } catch (err) {
      const st = err?.response?.status;
      const code = err?.response?.data?.code;
      const raw = err?.response?.data?.message || err?.response?.data?.detail;
      // FastAPI can answer with a detail list: only ever show text.
      const msg = typeof raw === "string" && raw !== "Not Found" ? raw : "";
      // Refusals another acknowledgement fixes: keep the dialog open and ask.
      const needs = refusalNeeds(code);
      if (needs.postedAck || needs.existingAck) {
        setPendingMove((cur) =>
          cur
            ? {
                ...cur,
                needPosted: cur.needPosted || needs.postedAck,
                needExisting: cur.needExisting || needs.existingAck,
                error: msg,
              }
            : cur,
        );
        return;
      }
      showToast(
        msg ||
          (st === 409
            ? "This note is signed or already posted to Athena, so it cannot be moved."
            : st === 404
              ? "Changing the visit is not available on this server yet."
              : "Could not move this note"),
        "error",
      );
      setPendingMove(null);
    } finally {
      setMovingId(null);
    }
  };

  // "Change provider": the provider the note is listed and signed under
  // (BlinkScribe PUT /v1/recording/{id}/primary-provider). Shown only when the
  // server supports it (its daily-review rows carry primary_provider). The
  // Athena appointment keeps its rendering provider.
  const primaryOverrideOn = supportsPrimaryOverride(notes);
  // The current override as a providerKey ("" when the visit's provider signs).
  const primaryKey = (note) => (primaryProviderName(note) ? keyOfRef(note.primary_provider) : "");
  const openProviderDialog = (note) =>
    setProviderDialog({ note, choice: primaryKey(note), saving: false, error: "" });
  const saveProvider = async (clear) => {
    const pd = providerDialog;
    if (!pd || pd.saving) return;
    const p = clear ? null : practiceProviders.find((x) => keyOf(x) === pd.choice);
    if (!clear && !p) return;
    setProviderDialog((cur) => (cur ? { ...cur, saving: true, error: "" } : cur));
    try {
      const res = await blinkscribeapi.put(`/v1/recording/${pd.note.id}/primary-provider`, primaryProviderBody(toRef(p)));
      if (res?.status === 200) {
        setProviderDialog(null);
        showToast(
          p
            ? `${p.providername} will sign this note. The Athena appointment keeps its provider.`
            : "This note signs under its visit's provider again.",
        );
        fetchNotes();
      } else {
        setProviderDialog((cur) => (cur ? { ...cur, saving: false, error: res?.message || "Could not change the provider" } : cur));
      }
    } catch (err) {
      const st = err?.response?.status;
      const raw = err?.response?.data?.message;
      const msg =
        typeof raw === "string" && raw && raw !== "Not Found"
          ? raw
          : st === 404
            ? "Changing the provider is not available on this server yet."
            : "Could not change the provider";
      setProviderDialog((cur) => (cur ? { ...cur, saving: false, error: msg } : cur));
    }
  };

  // Persist provider/discussed changes (note-level), fire-and-forget.
  const persistMeta = useCallback((note, meta) => {
    blinkscribeapi.put("/v1/recording/review-meta", {
      document_name: note.audiolink,
      providers: meta.providers,
      discussed_with: meta.discussedWith,
    }).catch((e) => console.warn("review-meta save failed", e));
  }, []);

  const updateMeta = (note, next) => {
    setMetaById((prev) => ({ ...prev, [note.id]: next }));
    persistMeta(note, next);
  };

  const addProvider = (note, p) => {
    const meta = getMeta(note);
    if (meta.providers.some((x) => same(x, p))) return;
    updateMeta(note, { ...meta, providers: [...meta.providers, toRef(p)] });
  };
  const removeProvider = (note, key) => {
    const meta = getMeta(note);
    updateMeta(note, { ...meta, providers: meta.providers.filter((x) => keyOfRef(x) !== key) });
  };
  const toggleDiscussed = (note, checked) => {
    const meta = getMeta(note);
    updateMeta(note, { ...meta, discussedWith: checked && defaultSupervisor ? toRef(defaultSupervisor) : null });
  };
  const setSupervisor = (note, key) => {
    const meta = getMeta(note);
    const p = practiceProviders.find((x) => keyOf(x) === key);
    if (!p) return;
    updateMeta(note, { ...meta, discussedWith: toRef(p) });
  };

  const handleSign = async (note) => {
    if (signingId) return;
    // Two unsigned notes on this visit: the second sign-off would overwrite
    // the first in Athena. Combine them first.
    const siblings = unsignedSiblingIds(notes, note);
    if (siblings.length) {
      showToast(combineFirstText(note, siblings), "warning");
      return;
    }
    // Recorded on another day than the visit it is filed to while the patient
    // had a visit that day: signing posts it into THAT visit's Athena
    // encounter (patient 8823, 09-29). Ask twice here when the mismatch is
    // visible; otherwise BlinkScribe's 409 VISIT_MISMATCH asks (signOffNote).
    let preConfirmed = false;
    if (needsSignMismatchConsent(note, visitOptsById[note.id])) {
      const ok = await requestSignMismatchConsent(mismatchDetailsFromNote(note, visitOptsById[note.id]));
      if (!ok) return;
      preConfirmed = true;
    }
    const meta = getMeta(note);
    setSigningId(note.id);
    try {
      const res = await signOffNote(
        {
          document_name: note.audiolink,
          generate_signed_pdf: false,
          providers: meta.providers,
          discussed_with: meta.discussedWith,
          save_review_meta: true,
        },
        { preConfirmed },
      );
      if (res?.cancelled) {
        showToast("Not signed.", "info");
      } else if (res?.status === 200 && res?.result) {
        setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, transcriptionstatus: "APPROVED", emrposted: true } : n)));
        showToast(res.partial ? "Signed — some sections did not post to Athena" : "Signed and posted to Athena", res.partial ? "warning" : "success");
      } else {
        showToast(res?.message || "Could not sign this note", "info");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Sign-off failed — note was not posted to the EHR", "error");
    } finally {
      setSigningId(null);
    }
  };

  const renderSOAP = (note, attestationLines) => {
    const soap = note?.note_json || {};
    const sections = Object.entries(SOAP_LABELS).filter(([k]) => soap[k]);
    if (!sections.length) return <div className="text-gray-400 text-sm p-6 text-center">No SOAP content for this note.</div>;
    return sections.map(([key, label]) => {
      const value = soap[key];
      const isAP = key === "assessment_plan";
      return (
        <div key={key} className="mb-4">
          <div className="text-xs font-bold uppercase tracking-wide text-[#1D2951] mb-1">{label}</div>
          <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {Array.isArray(value) ? (
              <FormattedNoteList items={value.map((v) => stripHtml(formatItem(v)))} numbered={isAP} />
            ) : (
              <FormattedNoteText text={stripHtml(typeof value === "object" ? formatItem(value) : value)} />
            )}
          </div>
          {isAP && attestationLines.length > 0 && (
            <div className="mt-2 text-sm text-[#1D4ED8] italic whitespace-pre-wrap leading-relaxed border-l-2 border-blue-200 pl-2">
              {attestationLines.join("\n")}
              <div className="text-[10px] not-italic text-gray-400 mt-0.5">Added to the note on sign</div>
            </div>
          )}
        </div>
      );
    });
  };

  const meta = selectedNote ? getMeta(selectedNote) : { providers: [], discussedWith: null };
  const attestationLines = selectedNote ? buildAttestations(meta.providers, meta.discussedWith) : [];
  const isSigned = selectedNote?.transcriptionstatus === "APPROVED";
  const assignable = practiceProviders.filter((p) => !meta.providers.some((x) => same(x, p)));
  const signSiblings = selectedNote ? unsignedSiblingIds(notes, selectedNote) : [];

  return (
    <div className="p-4 md:p-6">
      {/* View tabs — Billing is additive; Notes below is unchanged */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[["notes", "Notes"], ["billing", "Billing"]].map(([key, label]) => (
          <button key={key} onClick={() => setView(key)}
            className={`-mb-px px-4 py-2 text-sm font-semibold border-b-2 ${
              view === key ? "border-[#1D2951] text-[#1D2951]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {view === "billing" && <BillingTab showToast={showToast} />}

      {view === "notes" && (<>
      {/* Header / controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-[#1D2951] mr-auto">Note Review</h1>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient or note content…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
        />
        <DateField value={date} max={todayStr()} onChange={(v) => { setDate(v); setSelectedId(null); }} />
        {/* 9928's two locations; a one-location practice has nothing to filter. */}
        {hasMultipleDepartments() && <select value={deptFilter ?? ""} onChange={(e) => { setDeptFilter(e.target.value === "" ? "" : Number(e.target.value)); setSelectedId(null); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All departments</option>
          <option value="1">Odessa</option>
          <option value="2">Frisco</option>
        </select>}
        <select value={providerFilter} onChange={(e) => { setProviderFilter(e.target.value); setSelectedId(null); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[150px]">
          <option value="ALL">All providers</option>
          {dayProviders.map((p) => <option key={keyOf(p)} value={keyOf(p)}>{p.providername}</option>)}
        </select>
        <button onClick={fetchNotes} className="px-3 py-2 rounded-lg text-sm font-semibold bg-[#1D2951] text-white">Refresh</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b">
            {loading ? "Loading…" : `${filteredNotes.length} note${filteredNotes.length === 1 ? "" : "s"}`}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {!loading && filteredNotes.length === 0 && <div className="text-gray-400 text-sm p-6 text-center">No notes match.</div>}
            {filteredNotes.map((n) => {
              const active = selectedNote && n.id === selectedNote.id;
              return (
                <button key={n.id} onClick={() => setSelectedId(n.id)}
                  className={`w-full text-left px-4 py-3 border-b flex flex-col gap-1 ${active ? "bg-[#EEF2FB]" : "hover:bg-gray-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-gray-800 truncate">{n.patient_name || `Patient ${n.patientid || ""}`}</span>
                    <StatusBadge status={n.transcriptionstatus} />
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {n.providername || "—"}{n.starttime ? ` · ${n.starttime}` : ""}{n.purpose ? ` · ${n.purpose}` : ""}
                    {recorderTag(notes, n) ? ` · ${recorderTag(notes, n)}` : ""}
                  </div>
                  {noteVisitMismatch(n) && (
                    <span className="self-start px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                      Filed to the {shortDate((n.startdate || "").slice(0, 10))} visit
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[300px] flex flex-col">
          {!selectedNote ? (
            <div className="text-gray-400 text-sm p-10 text-center m-auto">Select a note to review.</div>
          ) : (
            <>
              {/* Title row */}
              <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b">
                <div className="mr-auto">
                  <div className="font-semibold text-[#1D2951]">{selectedNote.patient_name || `Patient ${selectedNote.patientid || ""}`}</div>
                  <div className="text-xs text-gray-500">{selectedNote.starttime ? `${selectedNote.starttime}` : ""}{selectedNote.purpose ? ` · ${selectedNote.purpose}` : ""}</div>
                </div>
                <StatusBadge status={selectedNote.transcriptionstatus} />
                <button onClick={() => setEditRecord({ id: selectedNote.id, patient_name: selectedNote.patient_name, audiolink: selectedNote.audiolink })}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#1D2951] text-[#1D2951]">Edit</button>
                {selectedNote.transcriptionstatus === "Summarized" ? (
                  <button onClick={() => handleSign(selectedNote)} disabled={signingId === selectedNote.id || signSiblings.length > 0}
                    title={signSiblings.length ? combineFirstText(selectedNote, signSiblings) : undefined}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${signingId === selectedNote.id || signSiblings.length ? "bg-gray-400 cursor-not-allowed" : "bg-[#1D2951]"}`}>
                    {signingId === selectedNote.id ? "Signing…" : "Sign & Post"}
                  </button>
                ) : isSigned ? <span className="px-3 py-2 text-sm font-semibold text-green-700">✅ Signed</span> : null}
              </div>
              {!isSigned && signSiblings.length > 0 && (
                <div className="px-5 py-2 border-b bg-red-50 text-sm text-red-800">{combineFirstText(selectedNote, signSiblings)}</div>
              )}

              {/* Visit: the appointment (and Athena encounter) this note is filed to */}
              {(() => {
                const vo = visitOptsById[selectedNote.id];
                const canPick =
                  !isSigned && vo && !vo.loading && !vo.error && vo.canMove !== false && (vo.options || []).length > 1;
                const currentId = vo?.current?.appointmentid || String(Number(selectedNote.appointmentid) || "");
                const mismatch = noteVisitMismatch(selectedNote);
                return (
                  <div className="px-5 py-3 border-b flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Visit</span>
                      {canPick ? (
                        <select
                          value={currentId}
                          disabled={!!movingId}
                          onChange={(e) => pickVisit(selectedNote, e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-sm max-w-full"
                          aria-label="Visit this note is filed to"
                        >
                          {vo.options.map((o) => (
                            <option key={o.appointmentid} value={o.appointmentid} disabled={o.disabled && !o.isCurrent}>
                              {optionText(o)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-sm text-gray-800">{noteVisitLabel(selectedNote)}</span>
                      )}
                      {mismatch && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Recorded {shortDate(recordingDayKey(selectedNote))}, not on this visit's day
                        </span>
                      )}
                      {vo?.loading && <span className="text-xs text-gray-400">Loading visits…</span>}
                      {movingId === selectedNote.id && <span className="text-xs text-gray-500">Moving…</span>}
                      {primaryOverrideOn && (
                        <span className="inline-flex flex-wrap items-center gap-2 ml-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider</span>
                          <span className="text-sm text-gray-800">
                            {selectedNote.providername || "—"}
                            {primaryProviderName(selectedNote)
                              ? ` (changed; visit provider ${visitProviderName(selectedNote) || "—"})`
                              : " (visit provider)"}
                          </span>
                          {!isSigned && (
                            <button onClick={() => openProviderDialog(selectedNote)}
                              className="px-2 py-1 rounded-lg text-xs font-semibold border border-[#1D2951] text-[#1D2951]">
                              Change provider
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {isSigned
                        ? "Signed notes stay on the visit they were signed to."
                        : "Sign & Post posts this note to this visit's Athena encounter. Changing the visit changes which encounter it posts to."}
                      {!isSigned && vo?.error ? ` ${vo.error}` : ""}
                      {!isSigned && vo && vo.canMove === false && vo.blockerText ? ` ${vo.blockerText}` : ""}
                    </div>
                  </div>
                );
              })()}

              {/* Provider(s) + discussed-with controls */}
              <div className="px-5 py-3 border-b bg-[#FAFBFF] flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider(s)</span>
                  {meta.providers.map((p, idx) => {
                    // The primary provider always tracks the appointment and is
                    // not removable; only extra co-providers can be removed.
                    const pKey = keyOfRef(p);
                    const isPrimary = idx === 0 || same(p, noteProvider(selectedNote));
                    return (
                      <span key={pKey || idx} className="inline-flex items-center gap-1 bg-[#E0E7FF] text-[#1D2951] text-xs font-semibold px-2 py-1 rounded-full">
                        {p.providername}
                        {isPrimary && (
                          <span className="text-[10px] font-normal text-[#6B7280]">
                            {primaryProviderName(selectedNote) ? "(signs)" : "(appt)"}
                          </span>
                        )}
                        {!isSigned && !isPrimary && <button onClick={() => removeProvider(selectedNote, pKey)} className="text-[#1D2951] hover:text-red-600 leading-none">×</button>}
                      </span>
                    );
                  })}
                  {!isSigned && (
                    <select value="" onChange={(e) => { const p = assignable.find((x) => keyOf(x) === e.target.value); if (p) addProvider(selectedNote, p); }}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs">
                      <option value="">+ Add provider</option>
                      {assignable.map((p) => <option key={keyOf(p)} value={keyOf(p)}>{p.providername}</option>)}
                    </select>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" disabled={isSigned} checked={!!meta.discussedWith}
                      onChange={(e) => toggleDiscussed(selectedNote, e.target.checked)} />
                    Discussed with
                  </label>
                  <select disabled={isSigned || !meta.discussedWith}
                    value={meta.discussedWith ? keyOfRef(meta.discussedWith) : (defaultSupervisor ? keyOf(defaultSupervisor) : "")}
                    onChange={(e) => setSupervisor(selectedNote, e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm disabled:opacity-50"
                    aria-label="Supervising physician">
                    {practiceProviders.map((p) => <option key={keyOf(p)} value={keyOf(p)}>{p.providername}</option>)}
                  </select>
                  <span className="text-xs text-gray-400">(supervising physician)</span>
                </div>
              </div>

              {/* Billing sanity check — orders vs LCD/rationale, accept/decline edits */}
              <SanityCheckPanel note={selectedNote} date={date} isSigned={isSigned}
                showToast={showToast} onApplied={fetchNotes} />

              {/* SOAP + live attestation preview */}
              <div className="p-5 overflow-y-auto" style={{ maxHeight: "56vh" }}>
                {renderSOAP(selectedNote, attestationLines)}
              </div>
            </>
          )}
        </div>
      </div>

      </>)}

      {/* Confirm a visit change: it decides which Athena encounter Sign & Post posts to. */}
      <Dialog open={!!pendingMove} onClose={() => { if (!movingId) setPendingMove(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1D2951" }}>Change the note's visit</DialogTitle>
        <DialogContent>
          {pendingMove ? (
            <>
              {moveConfirmText(pendingMove.note, pendingMove.target).map((line, i) => (
                <p key={i} className={`text-sm leading-relaxed ${i ? "mt-2 text-gray-700" : "font-semibold text-gray-900"}`}>{line}</p>
              ))}
              {pendingMove.needPosted && (
                <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-900 leading-relaxed">{postedAckText(pendingMove.note)}</p>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={pendingMove.ackPosted} onChange={(e) => updatePendingMove({ ackPosted: e.target.checked })} />}
                    label={<span className="text-sm">I understand; the office will correct that encounter</span>}
                  />
                </div>
              )}
              {pendingMove.needExisting && (
                <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-900 leading-relaxed">{existingAckText(pendingMove.target)}</p>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={pendingMove.ackExisting} onChange={(e) => updatePendingMove({ ackExisting: e.target.checked })} />}
                    label={<span className="text-sm">Move it anyway; the notes will be combined before signing</span>}
                  />
                </div>
              )}
              <TextField
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                label={pendingMove.needPosted ? "Reason (required)" : "Reason (optional)"}
                value={pendingMove.reason}
                onChange={(e) => updatePendingMove({ reason: e.target.value })}
                inputProps={{ "aria-label": "Reason for the move" }}
              />
              {pendingMove.error ? <p className="mt-2 text-sm text-red-700">{pendingMove.error}</p> : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingMove(null)} disabled={!!movingId} autoFocus>Cancel</Button>
          <Button variant="contained" onClick={confirmMove} disabled={!!movingId || !moveReady} sx={{ bgcolor: "#1D2951" }}>
            {movingId ? "Moving…" : "Move note"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change provider: who the note is listed and signed under (not the Athena rendering provider). */}
      <Dialog open={!!providerDialog} onClose={() => { if (!providerDialog?.saving) setProviderDialog(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1D2951" }}>Change provider</DialogTitle>
        <DialogContent>
          {providerDialog ? (
            <>
              <p className="text-sm text-gray-700 leading-relaxed">
                The note is listed and signed (attestation) under this provider. The Athena appointment keeps its
                rendering provider{visitProviderName(providerDialog.note) ? ` (${visitProviderName(providerDialog.note)})` : ""}.
              </p>
              <select
                value={providerDialog.choice}
                onChange={(e) => setProviderDialog((cur) => (cur ? { ...cur, choice: e.target.value, error: "" } : cur))}
                className="mt-3 w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                aria-label="Provider for this note"
              >
                <option value="">Choose a provider</option>
                {practiceProviders.map((p) => <option key={keyOf(p)} value={keyOf(p)}>{p.providername}</option>)}
              </select>
              {providerDialog.error ? <p className="mt-2 text-sm text-red-700">{providerDialog.error}</p> : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProviderDialog(null)} disabled={!!providerDialog?.saving} autoFocus>Cancel</Button>
          {providerDialog && primaryProviderName(providerDialog.note) ? (
            <Button onClick={() => saveProvider(true)} disabled={!!providerDialog.saving}>Use the visit's provider</Button>
          ) : null}
          <Button variant="contained" onClick={() => saveProvider(false)}
            disabled={!providerDialog || providerDialog.saving || !providerDialog.choice
              || providerDialog.choice === primaryKey(providerDialog.note)}
            sx={{ bgcolor: "#1D2951" }}>
            {providerDialog?.saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {editRecord && <Editscreen selectedRecord={editRecord} setIsEditScreen={() => { setEditRecord(null); fetchNotes(); }} />}
      <ToastMessage open={toast.open} message={toast.message} severity={toast.severity} handleClose={closeToast} />
    </div>
  );
};

export default NoteReviewScreen;
